import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Modal, Form, Spinner, Alert } from "react-bootstrap";
import PerfectScrollbar from "react-perfect-scrollbar";
import Select from "react-select";
import Swal from "sweetalert2";

import { getAllPaciente } from "../../../api/pacienteApi";
import { getUserCredentials } from "../../../api/usuarioApi";
import {
  addAnticipo,
  deleteAnticipo,
  editAnticipo,
  getAllAnticipos,
  getAnticiposByCierre,
  getAnticiposByOrdenDePago,
  getAnticiposDisponibles,
} from "../../../api/anticiposApi";
import { formatMiles } from "../../../utils/miles";

const emptyForm = {
  idPaciente: null,
  paciente: "",
  fecha: "",
  hora: "",
  recibo: "",
  comprobante: "",
  factura: "",
  monto: "",
  observacion: "",
  idcierre: "",
  idordendepago: "",
};

const getId = (row) => row?.idanticipo || row?.idAnticipo || row?.id;
const getPaciente = (row) => row?.paciente || row?.NombrePaciente || row?.nombrepaciente || "-";
const getMonto = (row) => Number(row?.monto || row?.Monto || 0);
const normalizeAmount = (value) => Number(String(value || "0").replace(/\./g, "").replace(",", ".")) || 0;
const formatMontoInput = (value) => {
  const clean = String(value || "").replace(/\D/g, "");
  return clean ? formatMiles(clean) : "";
};

const Anticipos = ({
  idcierre = null,
  idordendepago = null,
  idPaciente = null,
  pacienteNombre = "",
  soloDisponibles = false,
  embedded = false,
  cardTitle = "",
  cardSubtitle = "",
  onChange,
}) => {
  const [anticipos, setAnticipos] = useState([]);
  const [anticiposDisponibles, setAnticiposDisponibles] = useState([]);
  const [pacientes, setPacientes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showTraerModal, setShowTraerModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [current, setCurrent] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState({
    list: false,
    modal: false,
    disponibles: false,
    vinculando: false,
    saving: false,
    deleting: false,
  });

  const setLoad = (key, value) => setLoading((prev) => ({ ...prev, [key]: value }));
  const contextLabel = idcierre
    ? `Se vinculara al cierre #${idcierre}`
    : idordendepago
      ? `Se vinculara a la orden de pago #${idordendepago}`
      : "Se guardara sin cierre ni orden de pago";

  const fetchPacientes = useCallback(async () => {
    const result = await getAllPaciente();
    setPacientes(Array.isArray(result) ? result : []);
  }, []);

  const fetchAnticipos = useCallback(async () => {
    setLoad("list", true);
    setErrors({});
    try {
      let result;
      if (idcierre) {
        result = await getAnticiposByCierre(idcierre);
      } else if (idordendepago) {
        result = await getAnticiposByOrdenDePago(idordendepago);
      } else if (soloDisponibles) {
        result = await getAnticiposDisponibles();
      } else {
        result = await getAllAnticipos();
      }
      const list = Array.isArray(result) ? result : [];
      setAnticipos(list);
      if (onChange) onChange(list);
    } catch (err) {
      console.error("Error cargando anticipos:", err);
      setErrors({ list: "Error al cargar anticipos." });
      setAnticipos([]);
      if (onChange) onChange([]);
    } finally {
      setLoad("list", false);
    }
  }, [idcierre, idordendepago, soloDisponibles, onChange]);

  const fetchAnticiposDisponiblesPaciente = useCallback(async () => {
    if (!idcierre || !idPaciente) return;
    setLoad("disponibles", true);
    try {
      const result = await getAnticiposDisponibles();
      const list = Array.isArray(result) ? result : [];
      const pacienteId = String(idPaciente);
      const filtered = list.filter((anticipo) => {
        const anticipoPacienteId = anticipo.idPaciente || anticipo.IdPaciente;
        return String(anticipoPacienteId) === pacienteId;
      });
      setAnticiposDisponibles(filtered);
      setShowTraerModal(true);
    } catch (err) {
      console.error("Error cargando anticipos disponibles:", err);
      Swal.fire("Error", "No se pudieron cargar los anticipos disponibles.", "error");
    } finally {
      setLoad("disponibles", false);
    }
  }, [idcierre, idPaciente]);

  useEffect(() => {
    fetchAnticipos();
    fetchPacientes().catch((err) => console.error("Error cargando pacientes:", err));
  }, [fetchAnticipos, fetchPacientes]);

  const pacienteOptions = useMemo(
    () =>
      pacientes.map((pac) => ({
        value: pac.IdPacientes,
        label: pac.NombrePaciente,
      })),
    [pacientes]
  );

  const openModal = async (anticipo = null) => {
    setErrors({});
    if (!pacientes.length) {
      setLoad("modal", true);
      try {
        await fetchPacientes();
      } finally {
        setLoad("modal", false);
      }
    }

    if (anticipo) {
      setIsEditing(true);
      setCurrent(anticipo);
      setFormData({
        idPaciente: anticipo.idPaciente || anticipo.IdPaciente || null,
        paciente: getPaciente(anticipo),
        fecha: anticipo.fecha || "",
        hora: anticipo.hora || "",
        recibo: anticipo.recibo || "",
        comprobante: anticipo.comprobante || "",
        factura: anticipo.factura || "",
        monto: formatMiles(getMonto(anticipo)),
        observacion: anticipo.observacion || anticipo.obs || "",
        idcierre: anticipo.idcierre || anticipo.idLiquidacion || "",
        idordendepago: anticipo.idordendepago || "",
      });
    } else {
      setIsEditing(false);
      setCurrent(null);
      setFormData(emptyForm);
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setIsEditing(false);
    setCurrent(null);
    setFormData(emptyForm);
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "monto" ? formatMontoInput(value) : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const next = {};
    if (!formData.idPaciente) next.idPaciente = "Seleccione un paciente";
    if (!formData.fecha) next.fecha = "La fecha es requerida";
    if (!formData.hora) next.hora = "La hora es requerida";
    if (!formData.recibo.trim()) next.recibo = "El recibo es requerido";
    if (!formData.comprobante.trim()) next.comprobante = "El comprobante es requerido";
    if (!formData.factura.trim()) next.factura = "La factura es requerida";
    if (normalizeAmount(formData.monto) <= 0) next.monto = "El monto debe ser mayor a 0";
    return next;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validation = validate();
    if (Object.keys(validation).length) {
      setErrors(validation);
      return;
    }

    setLoad("saving", true);
    try {
      let user = null;
      try {
        user = await getUserCredentials();
      } catch {}

      const payload = {
        idanticipo: isEditing ? getId(current) : null,
        idPaciente: formData.idPaciente,
        paciente: formData.paciente,
        fecha: formData.fecha,
        hora: formData.hora,
        recibo: formData.recibo,
        comprobante: formData.comprobante,
        factura: formData.factura,
        monto: normalizeAmount(formData.monto),
        observacion: formData.observacion,
        idcierre: idcierre || null,
        idordendepago: idordendepago || null,
        quiencargo: user?.Usuario || user?.Nombre || user?.IdUsuario || null,
        quienedito: isEditing ? user?.Usuario || user?.Nombre || user?.IdUsuario || null : null,
      };

      if (isEditing) {
        await editAnticipo(payload);
      } else {
        await addAnticipo(payload);
      }
      await fetchAnticipos();
      closeModal();
      Swal.fire({
        icon: "success",
        title: isEditing ? "Anticipo actualizado" : "Anticipo agregado",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error("Error guardando anticipo:", err);
      setErrors({ submit: err.message || "Error al guardar el anticipo." });
    } finally {
      setLoad("saving", false);
    }
  };

  const handleDelete = async (anticipo) => {
    const result = await Swal.fire({
      title: "Estas seguro?",
      text: "No podras revertir esta accion.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Si, eliminar",
      cancelButtonText: "Cancelar",
    });
    if (!result.isConfirmed) return;

    setLoad("deleting", true);
    try {
      await deleteAnticipo(getId(anticipo));
      await fetchAnticipos();
      Swal.fire("Eliminado", "El anticipo ha sido eliminado.", "success");
    } catch (err) {
      console.error("Error eliminando anticipo:", err);
      setErrors({ list: err.message || "Error al eliminar el anticipo." });
    } finally {
      setLoad("deleting", false);
    }
  };

  const handleVincularAnticipo = async (anticipo) => {
    setLoad("vinculando", true);
    try {
      let user = null;
      try {
        user = await getUserCredentials();
      } catch {}

      await editAnticipo({
        idanticipo: getId(anticipo),
        idPaciente: anticipo.idPaciente || anticipo.IdPaciente || idPaciente,
        paciente: getPaciente(anticipo),
        fecha: anticipo.fecha || null,
        hora: anticipo.hora || null,
        recibo: anticipo.recibo || "",
        comprobante: anticipo.comprobante || "",
        factura: anticipo.factura || "",
        monto: getMonto(anticipo),
        observacion: anticipo.observacion || anticipo.obs || "",
        idcierre,
        idordendepago: null,
        quienedito: user?.Usuario || user?.Nombre || user?.IdUsuario || null,
      });

      await fetchAnticipos();
      setShowTraerModal(false);
      setAnticiposDisponibles([]);
      Swal.fire({
        icon: "success",
        title: "Anticipo vinculado",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error("Error vinculando anticipo:", err);
      Swal.fire("Error", err.message || "No se pudo vincular el anticipo.", "error");
    } finally {
      setLoad("vinculando", false);
    }
  };

  const totalAnticipos = anticipos.reduce((acc, row) => acc + getMonto(row), 0);
  const canTraerAnticipos = Boolean(idcierre && idPaciente);
  const tableHeaderStyle = {
    backgroundColor: "#0d9fe8",
    color: "#fff",
    fontWeight: 600,
  };
  const useCardShell = !embedded || Boolean(cardTitle);
  const headerActions = (
    <div className="d-flex gap-2 align-items-center flex-wrap">
      <Button
        variant="outline-warning"
        size="sm"
        onClick={() => openModal()}
        disabled={loading.modal}
        title="Agregar Anticipo"
      >
        <i className="fas fa-dollar-sign"></i>
      </Button>
      {canTraerAnticipos && (
        <Button
          variant="outline-warning"
          size="sm"
          onClick={fetchAnticiposDisponiblesPaciente}
          disabled={loading.disponibles}
          title="Traer Anticipo"
        >
          <i className="fas fa-list"></i>
        </Button>
      )}
      <Button
        variant="outline-primary"
        size="sm"
        onClick={fetchAnticipos}
        disabled={loading.list}
        title="Refrescar"
      >
        <i className="fas fa-sync-alt"></i>
      </Button>
      {soloDisponibles && (
        <span className="badge bg-light text-dark border">
          Solo sin cierre vinculado
        </span>
      )}
    </div>
  );

  return (
    <div className={useCardShell ? "card h-100" : ""}>
      {useCardShell ? (
        <div className="card-header d-flex justify-content-between align-items-center">
          {cardTitle ? (
            <h5 className="mb-0">
              {cardTitle}{" "}
              {cardSubtitle && <small className="text-muted ms-2">{cardSubtitle}</small>}
            </h5>
          ) : (
            <span />
          )}
          {headerActions}
        </div>
      ) : (
        <div className="mb-2">{headerActions}</div>
      )}

      <div className={useCardShell ? "card-body p-2" : ""}>
        {errors.list && <Alert variant="danger">{errors.list}</Alert>}
        {loading.list ? (
          <div className="text-center">
            <Spinner animation="border" role="status" />
          </div>
        ) : (
          <PerfectScrollbar>
            <table className="table table-sm mb-0">
              <thead>
                <tr>
                  <th style={tableHeaderStyle}>Paciente</th>
                  <th style={tableHeaderStyle}>Recibo</th>
                  <th style={tableHeaderStyle}>Comprobante</th>
                  <th style={tableHeaderStyle}>Factura</th>
                  <th className="text-end" style={tableHeaderStyle}>Monto</th>
                  <th style={tableHeaderStyle}>Observacion</th>
                  <th className="text-center" style={tableHeaderStyle}>Opc.</th>
                </tr>
              </thead>
              <tbody>
                {anticipos.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center text-muted">
                      Sin registros
                    </td>
                  </tr>
                ) : (
                  anticipos.map((anticipo) => (
                    <tr key={getId(anticipo)}>
                      <td>{getPaciente(anticipo)}</td>
                      <td>{anticipo.recibo || "-"}</td>
                      <td>{anticipo.comprobante || "-"}</td>
                      <td>{anticipo.factura || "-"}</td>
                      <td className="text-end fw-semibold">{formatMiles(getMonto(anticipo))}</td>
                      <td>{anticipo.observacion || anticipo.obs || "-"}</td>
                      <td className="text-center">
                        <div className="d-flex justify-content-center">
                          <button className="btn btn-primary btn-sm me-1" onClick={() => openModal(anticipo)}>
                            <i className="fas fa-pencil-alt"></i>
                          </button>
                          <button className="btn btn-danger btn-sm" onClick={() => handleDelete(anticipo)} disabled={loading.deleting}>
                            <i className="fa fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td className="text-uppercase fw-bold">Total</td>
                  <td colSpan={3}></td>
                  <td className="text-end fw-bold">{formatMiles(totalAnticipos)}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </PerfectScrollbar>
        )}
      </div>

      <Modal show={showModal} onHide={closeModal}>
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? "Editar Anticipo" : "Agregar Anticipo"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {errors.submit && <Alert variant="danger">{errors.submit}</Alert>}
          <Alert variant={idcierre || idordendepago ? "info" : "secondary"} className="py-2">
            {contextLabel}
          </Alert>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Paciente <span className="text-danger">*</span></Form.Label>
              <Select
                options={pacienteOptions}
                isLoading={!pacientes.length}
                placeholder="Seleccione un paciente..."
                value={pacienteOptions.find((option) => option.value === formData.idPaciente) || null}
                onChange={(option) => {
                  setFormData((prev) => ({
                    ...prev,
                    idPaciente: option?.value || null,
                    paciente: option?.label || "",
                  }));
                  setErrors((prev) => ({ ...prev, idPaciente: "" }));
                }}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
              {errors.idPaciente && <Form.Text className="text-danger">{errors.idPaciente}</Form.Text>}
            </Form.Group>

            <div className="row">
              <Form.Group className="mb-3 col-md-6">
                <Form.Label>Fecha *</Form.Label>
                <Form.Control type="date" name="fecha" value={formData.fecha} onChange={handleInputChange} />
                {errors.fecha && <Form.Text className="text-danger">{errors.fecha}</Form.Text>}
              </Form.Group>
              <Form.Group className="mb-3 col-md-6">
                <Form.Label>Hora *</Form.Label>
                <Form.Control type="time" name="hora" value={formData.hora} onChange={handleInputChange} />
                {errors.hora && <Form.Text className="text-danger">{errors.hora}</Form.Text>}
              </Form.Group>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Monto *</Form.Label>
              <Form.Control type="text" name="monto" value={formData.monto} onChange={handleInputChange} placeholder="Monto del anticipo" />
              {errors.monto && <Form.Text className="text-danger">{errors.monto}</Form.Text>}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Recibo *</Form.Label>
              <Form.Control type="text" name="recibo" value={formData.recibo} onChange={handleInputChange} />
              {errors.recibo && <Form.Text className="text-danger">{errors.recibo}</Form.Text>}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Comprobante *</Form.Label>
              <Form.Control type="text" name="comprobante" value={formData.comprobante} onChange={handleInputChange} />
              {errors.comprobante && <Form.Text className="text-danger">{errors.comprobante}</Form.Text>}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Factura *</Form.Label>
              <Form.Control type="text" name="factura" value={formData.factura} onChange={handleInputChange} />
              {errors.factura && <Form.Text className="text-danger">{errors.factura}</Form.Text>}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Observacion</Form.Label>
              <Form.Control as="textarea" rows={3} name="observacion" value={formData.observacion} onChange={handleInputChange} />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={closeModal} disabled={loading.saving}>
            Cerrar
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={loading.saving}>
            {loading.saving ? "Guardando..." : isEditing ? "Actualizar" : "Agregar"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showTraerModal} onHide={() => setShowTraerModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Traer Anticipo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="py-2">
            Anticipos sin cierre vinculado para {pacienteNombre || `paciente #${idPaciente}`}.
          </Alert>
          {anticiposDisponibles.length === 0 ? (
            <p className="mb-0">No hay anticipos disponibles para este paciente.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm mb-0">
                <thead>
                  <tr>
                    <th>Recibo</th>
                    <th>Comprobante</th>
                    <th>Factura</th>
                    <th className="text-end">Monto</th>
                    <th>Observacion</th>
                    <th className="text-center">Opc.</th>
                  </tr>
                </thead>
                <tbody>
                  {anticiposDisponibles.map((anticipo) => (
                    <tr key={getId(anticipo)}>
                      <td>{anticipo.recibo || "-"}</td>
                      <td>{anticipo.comprobante || "-"}</td>
                      <td>{anticipo.factura || "-"}</td>
                      <td className="text-end fw-semibold">{formatMiles(getMonto(anticipo))}</td>
                      <td>{anticipo.observacion || anticipo.obs || "-"}</td>
                      <td className="text-center">
                        <Button
                          size="sm"
                          variant="success"
                          onClick={() => handleVincularAnticipo(anticipo)}
                          disabled={loading.vinculando}
                        >
                          Vincular
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={() => setShowTraerModal(false)} disabled={loading.vinculando}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Anticipos;
